<template>
  <div class="p-padding-24 w-[450px]">
    <div class="pb-padding-16 border-bg-divider flex items-center justify-between border-b">
      <div class="gap-size-10 flex items-center">
        <span class="text-font-16 font-semibold"> 새알림 </span>
        <span class="text-font-16 text-red font-semibold">{{ unreadTotalCount }}</span>
      </div>
      <div>
        <a class="text-font-14 cursor-pointer underline" @click.prevent="goToNotificationCenter">
          전체보기
        </a>
      </div>
    </div>
    <div class="pt-padding-16">
      <!-- 로딩 상태 -->
      <div v-if="loading" class="py-padding-32 flex items-center justify-center">
        <span class="text-font-14 text-default-muted">로딩 중</span>
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="error" class="py-padding-32 flex items-center justify-center">
        <span class="text-font-14 text-red">{{ error }}</span>
      </div>

      <!-- 빈 상태 -->
      <div
        v-else-if="unreadIsEmpty"
        class="py-padding-32 flex min-h-[500px] items-center justify-center"
      >
        <span class="text-font-14 text-default-muted">새로운 알림이 없습니다.</span>
      </div>

      <!-- 알림 목록 (최대 5개 높이, 스크롤 가능) -->
      <div
        v-else
        ref="scrollContainer"
        class="max-h-[500px] overflow-y-auto"
        @scroll="handleScroll"
      >
        <BaseList>
          <BaseListItem
            v-for="notification in unreadNotifications"
            :key="notification.id"
            clickable
            @click="handleNotificationClick(notification)"
          >
            <template #content>
              <div class="gap-padding-16 flex min-w-0">
                <div class="gap-size-12 flex min-w-0 flex-1">
                  <div class="gap-size-6 flex min-w-0 flex-1 flex-col overflow-hidden">
                    <span
                      class="text-font-16 overflow-hidden text-ellipsis whitespace-nowrap font-bold"
                    >
                      {{ notification.subject || '알림' }}
                    </span>
                    <span class="text-font-16 line-clamp-2 overflow-hidden">
                      {{ notification.contents || '' }}
                    </span>
                    <span
                      class="text-font-14 text-default-muted overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                      {{ formatDate(notification.sendAt) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </BaseListItem>
        </BaseList>

        <!-- 추가 로딩 인디케이터 -->
        <div v-if="isLoadingMore" class="py-padding-16 flex items-center justify-center">
          <span class="text-font-14 text-default-muted">로딩 중</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  useNotification,
  type UnreadNotificationListItem,
} from '@/composables/notification/useNotification';
import { BaseList, BaseListItem } from '@template/ui';
import { onMounted, nextTick, ref } from 'vue';
import { useRouter } from 'vue-router';

interface Props {
  /**
   * Popover 닫기 함수
   */
  close?: () => void;
}

const props = defineProps<Props>();

const router = useRouter();

// 스크롤 컨테이너 참조
const scrollContainer = ref<HTMLElement>();

// 페이지네이션 상태
const currentPage = ref(0);
const pageSize = ref(20);
const isLoadingMore = ref(false);
const hasReachedEnd = ref(false);

// 알림 관리
const {
  loading,
  error,
  unreadNotifications,
  unreadTotalCount,
  unreadIsEmpty,
  unreadIsLast,
  loadUnreadNotifications,
  markAsChecked,
} = useNotification();

/**
 * 알림 클릭 핸들러
 */
const handleNotificationClick = async (notification: UnreadNotificationListItem) => {
  try {
    // 알림을 읽음 처리
    await markAsChecked(notification.id);

    // Popover 닫기
    if (props.close) {
      props.close();
      await nextTick();
    }

    // 링크가 있으면 해당 링크로 이동
    if (notification.webLink) {
      router.push(notification.webLink);
    } else if (notification.appLink) {
      router.push(notification.appLink);
    }
  } catch (err) {
    console.error('알림 처리 실패:', err);
  }
};

/**
 * 알림 센터로 이동
 */
const goToNotificationCenter = async () => {
  // Popover를 먼저 닫기
  if (props.close) {
    props.close();
    // 닫기 처리가 완료될 때까지 대기
    await nextTick();
  }
  // 그 다음 라우터 이동
  router.push({ name: 'mypage', params: { menuTab: 'notification' } });
};

/**
 * 스크롤 이벤트 핸들러 (무한 스크롤)
 */
const handleScroll = async () => {
  if (!scrollContainer.value || isLoadingMore.value || hasReachedEnd.value) {
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  // 스크롤이 하단 100px 이내로 도달하면 다음 페이지 로드
  const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

  if (isNearBottom) {
    await loadMoreNotifications();
  }
};

/**
 * 다음 페이지 알림 로드
 */
const loadMoreNotifications = async () => {
  try {
    isLoadingMore.value = true;
    const nextPage = currentPage.value + 1;

    await loadUnreadNotifications({
      page: nextPage,
      size: pageSize.value,
    });

    currentPage.value = nextPage;

    // unreadIsLast를 통해 마지막 페이지 여부 확인
    if (unreadIsLast.value) {
      hasReachedEnd.value = true;
    }
  } catch (err) {
    console.error('추가 알림 로드 실패:', err);
  } finally {
    isLoadingMore.value = false;
  }
};

/**
 * 날짜 포맷팅
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  if (diffInMinutes < 1) return '방금 전';
  if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}시간 전`;

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  // 올해인 경우
  if (date.getFullYear() === now.getFullYear()) {
    return `${month}/${day} ${hours}:${minutes}`;
  }

  // 올해가 아닌 경우
  return `${date.getFullYear()}/${month}/${day}`;
};

/**
 * 초기 로드
 */
onMounted(async () => {
  // 첫 페이지 로드
  await loadUnreadNotifications({
    page: currentPage.value,
    size: pageSize.value,
  });

  // 첫 로드 후 마지막 페이지 여부 확인
  if (unreadIsLast.value) {
    hasReachedEnd.value = true;
  }
});
</script>
